import { Subject } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { JobCard } from '@job-card/models/job-card';
import { JobCardStorageService } from '@job-card/services/job-card-storage/job-card-storage.service';
import { ScheduledAssessment, ScheduledAssessmentStatus } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { LeadAssessmentService } from '@leads/shared/services/lead-assessment/lead-assessment.service';
import { LeadStorageService } from '@leads/shared/services/lead-storage/lead-storage.service';
import { CalendarEventColours } from '@schedule/lib/classes/event-colours';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { AlertTypes } from '@shared/models/alert';
import { IStorageFile } from '@shared/models/storage';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

import { JobCardAttachmentComponent } from '../job-card-attachment/job-card-attachment.component';
import { JobCardSignatureComponent } from '../job-card-signature/job-card-signature.component';

@Component({
	selector: 'app-job-card-view-and-sign-offcanvas',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		LoadingStateComponent,
		JobCardAttachmentComponent,
		JobCardSignatureComponent,
		AlertComponent,
	],
	templateUrl: './job-card-view-and-sign-offcanvas.component.html',
	styleUrl: './job-card-view-and-sign-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class JobCardViewAndSignOffcanvasComponent implements OnInit {
	@Input() scheduledAssessment: ScheduledAssessment;
	@Input() scheduledAssessmentId: string;
	@Input() mustRefreshAttachments: Subject<void>;

	private offcanvasService = inject(OffcanvasService);
	private toastService = inject(ToastService);
	private jobCardStorage = inject(JobCardStorageService);
	private leadStorage = inject(LeadStorageService);
	private leadAssessmentService = inject(LeadAssessmentService);

	AlertTypes = AlertTypes;

	attachments: Array<IStorageFile> = [];
	isLoading = false;
	isSaving = false;

	ngOnInit(): void {
		this.fetchAttachments();
	}

	fetchAttachments(): void {
		this.isLoading = true;
		this.jobCardStorage
			.getJobCardAttachments(
				this.scheduledAssessment.organisationId,
				this.scheduledAssessment.leadRequestId,
				this.scheduledAssessmentId,
			)
			.then((attachments) => {
				this.attachments = attachments;
			})
			.catch(() => {
				this.toastService.showToast({
					title: 'Error',
					message: 'Failed to load attachments. Please try again later.',
					type: ToastTypes.ERROR,
				});
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	getScheduledAssessmentColour(status: ScheduledAssessmentStatus): string {
		return CalendarEventColours.scheduledAssessment(status).secondary;
	}

	close(): void {
		this.offcanvasService.dismissLast();
	}

	signatureSaved(signature: Blob): void {
		this.isLoading = true;
		const signaturePromise = this.leadStorage.uploadCustomerAssessmentSignature(
			this.scheduledAssessment.organisationId,
			this.scheduledAssessment.leadRequestId,
			this.scheduledAssessmentId,
			this.scheduledAssessment.customerName,
			signature,
		);

		const leadAssessmentPromise = this.leadAssessmentService.update(this.scheduledAssessmentId, {
			signedJobCard: {
				assessmentDetails: this.scheduledAssessment.assessmentDetails,
				jobCard: this.scheduledAssessment.jobCard,
			},
		});

		const lockJobCardAttachmentsPromise = this.jobCardStorage.lockJobCardAttachments(
			this.scheduledAssessment.organisationId,
			this.scheduledAssessment.leadRequestId,
			this.scheduledAssessmentId,
		);

		Promise.all([signaturePromise, leadAssessmentPromise, lockJobCardAttachmentsPromise])
			.then(() => {
				this.toastService.showToast({
					title: 'Job Card',
					message: 'Job Card Signed',
					type: ToastTypes.SUCCESS,
				});
			})
			.catch(() => {
				this.toastService.showToast({
					title: 'Job Card',
					message: 'Failed to sign job card',
					type: ToastTypes.ERROR,
				});
			})
			.finally(() => {
				this.isLoading = false;
				this.mustRefreshAttachments.next();
				this.offcanvasService.dismissLast();
			});
	}

	get jobCardActive(): boolean {
		if (this.scheduledAssessment.signedJobCard) {
			return false;
		}

		return !this.jobCard.startTime || !this.jobCard.endTime;
	}

	get scheduledTime(): string {
		const options: Intl.DateTimeFormatOptions = {
			weekday: 'short',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		};

		if (this.jobCardActive) {
			return 'Job is still active';
		}

		return Intl.DateTimeFormat('en-ZA', options).formatRange(this.jobCard.startTime!.toDate(), this.jobCard.endTime!.toDate());
	}

	get jobCard(): JobCard {
		if (this.scheduledAssessment.signedJobCard) {
			return this.scheduledAssessment.signedJobCard.jobCard;
		}
		return this.scheduledAssessment.jobCard;
	}

	get assessmentDetails(): ScheduledAssessment['assessmentDetails'] {
		if (this.scheduledAssessment.signedJobCard) {
			return this.scheduledAssessment.signedJobCard.assessmentDetails;
		}
		return this.scheduledAssessment.assessmentDetails;
	}

	get filteredAttachments(): Array<IStorageFile> {
		if (this.scheduledAssessment.signedJobCard) {
			return this.attachments.filter((attachment) => attachment.customMetadata?.locked);
		}
		return this.attachments;
	}
}
